import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLang } from '../context/LangContext';

const content = {
  en: {
    pageTitle: 'Terms & Conditions',
    metaTitle: 'Terms & Conditions | Halo Aesthetic Clinic Richmond Hill',
    metaDesc: 'Review the Terms and Conditions for medical aesthetic treatments, consultations, and services at Halo Aesthetic Clinic Richmond Hill.',
    lastUpdated: 'Last Updated: January 2026',
    sections: [
      {
        title: 'Agreement to Terms',
        content: 'By accessing and using the services of Halo Aesthetic Clinic ("we," "our," or "us"), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.'
      },
      {
        title: 'Services Description',
        content: 'Halo Aesthetic Clinic provides professional medical aesthetic services including but not limited to:',
        list: [
          'Facial treatments and skin rejuvenation',
          'Anti-aging procedures',
          'Body contouring treatments',
          'Laser treatments',
          'Injectable treatments (Botox, fillers)',
          'Consultation and assessment services'
        ]
      },
      {
        title: 'Refund & Cancellation Policy (Medical Aesthetic Services)',
        content: 'Refund Policy: All medical aesthetic services are provided under a written treatment service agreement. Services that have already been rendered are non-refundable. For treatment packages, any unused and unrendered services may be eligible for a refund or credit, subject to the following terms:\n\nCancellation Policy: Clients may cancel future appointments with a minimum of 24 hours\' notice. Missed or late-cancelled appointments may be subject to a cancellation fee. This policy does not affect a client\'s statutory rights under applicable consumer protection laws.',
        list: [
          'Refund requests must be submitted in writing.',
          'Refunds apply only to the unused portion of the treatment package.',
          'An administrative fee may apply where permitted by law.',
          'Approved refunds will be processed to the original form of payment within 10–14 business days.',
          'If a treatment package has been partially completed, the refund will be calculated by deducting the regular price of the services already rendered, with the remaining balance refunded accordingly.'
        ]
      },
      {
        title: 'Payment Terms',
        content: 'Payment policies:',
        list: [
          'Payment is due at the time of service unless other arrangements have been made',
          'We accept cash, credit cards, and approved financing options',
          'All prices are subject to applicable taxes',
          'Package deals and promotions are subject to specific terms and conditions'
        ]
      },
      {
        title: 'Medical Consultation and Consent',
        content: 'Important medical considerations:',
        list: [
          'All treatments require a consultation with our qualified medical professionals',
          'You must provide accurate medical history and current medications',
          'Informed consent must be obtained before any treatment',
          'Some treatments may not be suitable for all individuals',
          'Results may vary and are not guaranteed'
        ]
      },
      {
        title: 'Health and Safety',
        content: 'Safety requirements:',
        list: [
          'You must follow all pre and post-treatment instructions',
          'Report any adverse reactions or concerns immediately',
          'Maintain good hygiene and follow our clinic protocols',
          'Inform us of any changes in your health status',
          'Follow-up appointments may be required for optimal results'
        ]
      },
      {
        title: 'Age Restrictions',
        content: 'Age requirements:',
        list: [
          'You must be at least 18 years old to receive treatments without parental consent',
          'Minors (under 18) require parental or guardian consent and accompaniment',
          'Some treatments may have additional age restrictions',
          'Valid identification may be required'
        ]
      },
      {
        title: 'Liability and Disclaimers',
        content: 'Important disclaimers:',
        list: [
          'All treatments carry inherent risks and potential side effects',
          'We are not liable for results that do not meet your expectations',
          'Individual results may vary based on various factors',
          'We recommend consulting with your primary healthcare provider for medical concerns',
          'Our liability is limited to the cost of the treatment provided'
        ]
      },
      {
        title: 'Intellectual Property',
        content: 'All content on our website, including text, images, logos, and trademarks, is the property of Halo Aesthetic Clinic and is protected by copyright and trademark laws. You may not reproduce, distribute, or use our content without written permission.'
      },
      {
        title: 'Privacy and Confidentiality',
        content: 'We are committed to maintaining the confidentiality of your personal and medical information in accordance with applicable privacy laws and our Privacy Policy.'
      },
      {
        title: 'Modifications to Terms',
        content: 'We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting on our website. Your continued use of our services constitutes acceptance of the modified terms.'
      },
      {
        title: 'Governing Law',
        content: 'These Terms and Conditions are governed by the laws of Ontario, Canada. Any disputes will be resolved in the courts of Ontario.'
      },
      {
        title: 'Contact Information',
        content: 'If you have any questions about these Terms and Conditions, please contact us:',
        contact: {
          email: 'info@haloaestheticclinic.com',
          phone: '942-388-1688',
          address: '1595 16th Ave Suite 103, Richmond Hill, ON L4B 3N9'
        }
      }
    ]
  },
  cn: {
    pageTitle: '条款与条件',
    metaTitle: '服务条款与须知 | Halo Richmond Hill 医美诊所',
    metaDesc: '了解 Halo Richmond Hill 医美诊所关于医疗美容疗程服务、预约及退款取消的相关条款与须知。',
    lastUpdated: '最后更新：2026年1月',
    sections: [
      {
        title: '协议条款',
        content: '通过访问和使用Halo Aesthetic Clinic（"我们"、"我们的"或"我们"）的服务，您同意受这些条款和条件的约束。如果您不同意这些条款，请不要使用我们的服务。'
      },
      {
        title: '服务描述',
        content: 'Halo Aesthetic Clinic提供专业的医美服务，包括但不限于：',
        list: [
          '面部护理和肌肤焕新',
          '抗衰老程序',
          '身体塑形治疗',
          '激光治疗',
          '注射治疗（肉毒素、填充剂）',
          '咨询和评估服务'
        ]
      },
      {
        title: '退款与取消政策（医疗美容服务）',
        content: '退款政策：所有医疗美容服务均在签署书面疗程服务协议的前提下提供。已完成并已提供的服务不予退款。对于疗程套餐，任何尚未使用且尚未提供的服务，可在符合以下条款的情况下申请退款或转为服务额度：\n\n取消政策：客户如需取消已预约但尚未进行的服务，须至少提前24小时通知。未按时取消或缺席预约的，可能会收取相应的取消费用。本政策不影响客户依据适用的消费者保护法律所享有的法定权利。',
        list: [
          '退款申请须以书面形式提交。',
          '退款仅适用于疗程套餐中未使用的部分。',
          '在法律允许的范围内，退款可能会收取合理的行政处理费用。',
          '经批准的退款将在10–14个工作日内原路退回至最初的付款方式。',
          '如疗程套餐已部分完成，退款将按照已完成疗程的原价进行扣除，剩余未使用的金额将相应退还。'
        ]
      },
      {
        title: '付款条款',
        content: '付款政策：',
        list: [
          '除非另有安排，否则服务时需付款',
          '我们接受现金、信用卡和批准的融资选项',
          '所有价格均需缴纳适用税费',
          '套餐优惠和促销活动受特定条款和条件约束'
        ]
      },
      {
        title: '医疗咨询和同意',
        content: '重要医疗考虑：',
        list: [
          '所有治疗都需要与我们合格的医疗专业人员进行咨询',
          '您必须提供准确的医疗历史和当前用药情况',
          '任何治疗前都必须获得知情同意',
          '某些治疗可能不适合所有个人',
          '结果可能有所不同，不保证效果'
        ]
      },
      {
        title: '健康和安全',
        content: '安全要求：',
        list: [
          '您必须遵循所有治疗前和治疗后的指示',
          '立即报告任何不良反应或担忧',
          '保持良好的卫生习惯并遵循我们的诊所协议',
          '告知我们您健康状况的任何变化',
          '可能需要后续预约以获得最佳效果'
        ]
      },
      {
        title: '年龄限制',
        content: '年龄要求：',
        list: [
          '您必须年满18岁才能在未经父母同意的情况下接受治疗',
          '未成年人（18岁以下）需要父母或监护人的同意和陪同',
          '某些治疗可能有额外的年龄限制',
          '可能需要有效的身份证明'
        ]
      },
      {
        title: '责任和免责声明',
        content: '重要免责声明：',
        list: [
          '所有治疗都有固有风险和潜在副作用',
          '我们不对不符合您期望的结果承担责任',
          '个人结果可能因各种因素而异',
          '我们建议就医疗问题咨询您的主要医疗保健提供者',
          '我们的责任仅限于所提供治疗的费用'
        ]
      },
      {
        title: '知识产权',
        content: '我们网站上的所有内容，包括文本、图像、徽标和商标，都是Halo Aesthetic Clinic的财产，受版权和商标法保护。未经书面许可，您不得复制、分发或使用我们的内容。'
      },
      {
        title: '隐私和保密',
        content: '我们致力于根据适用的隐私法律和我们的隐私政策维护您个人和医疗信息的机密性。'
      },
      {
        title: '条款修改',
        content: '我们保留随时修改这些条款和条件的权利。更改将在我们网站上发布后立即生效。您继续使用我们的服务即表示接受修改后的条款。'
      },
      {
        title: '适用法律',
        content: '这些条款和条件受加拿大安大略省法律管辖。任何争议将在安大略省法院解决。'
      },
      {
        title: '联系信息',
        content: '如果您对这些条款和条件有任何疑问，请通过以下方式联系我们：',
        contact: {
          email: 'info@haloaestheticclinic.com',
          phone: '942-388-1688',
          address: '1595 16th Ave Suite 103, Richmond Hill, ON L4B 3N9'
        }
      }
    ]
  }
};

const TermsConditions = () => {
  const { lang } = useLang();
  const c = content[lang];

  const canonicalUrl = 'https://www.haloaestheticclinic.com/terms-conditions';

  return (
    <div className="bg-softGradient bg-no-repeat bg-top text-text">
      <Helmet>
        <title>{c.metaTitle}</title>
        <meta name="description" content={c.metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={c.metaTitle} />
        <meta property="og:description" content={c.metaDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* 页面标题 */}
      <section className="py-section max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-gray-900">{c.pageTitle}</h1>
          <p className="text-lg text-gray-600">{c.lastUpdated}</p>
        </div>

        {/* 内容区域 */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-card p-8 border border-champagne/40">
          <div className="space-y-8">
            {c.sections.map((section, index) => (
              <div key={index} className="border-b border-champagne/30 pb-6 last:border-b-0">
                <h2 className="text-2xl font-bold text-[#2E6B4C] mb-4">{section.title}</h2>
                <p className="text-gray-700 mb-4 leading-relaxed whitespace-pre-line">{section.content}</p>
                
                {section.list && (
                  <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                    {section.list.map((item, itemIndex) => (
                      <li key={itemIndex} className="leading-relaxed">{item}</li>
                    ))}
                  </ul>
                )}
                
                {section.contact && (
                  <div className="mt-4 p-4 bg-softGold/20 rounded-lg border border-champagne/40">
                    <p className="text-gray-700 mb-2"><strong>Email:</strong> {section.contact.email}</p>
                    <p className="text-gray-700 mb-2"><strong>Phone:</strong> <a href="tel:942-388-1688" className="hover:text-[#2E6B4C] transition-colors">{section.contact.phone}</a></p>
                    <p className="text-gray-700"><strong>Address:</strong> {section.contact.address}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;
